import type { AxiosInstance } from 'axios';

/** Unified rule draft generation (classification, supplier_alias, VAT suggestions). Legacy per-family URLs remain supported. */
export const RULE_PROPOSALS_V2_GENERATE_URL =
  '/api/transaction-classification-rules/rule-proposals/v2/generate';

export type RuleProposalFamilyV2 = 'classification' | 'supplier_alias' | 'vat_suggestion';

export type PostRuleProposalsV2Options = {
  userId?: number | null;
  account_scope?: string;
  /** Uses stored audit snapshot + bucket ids; server unions sample transaction IDs. */
  auditCandidates?: { auditRunId: string; candidateIds: string[] };
  /**
   * Relax global dedupe suppression for supplier/classification IDs flow (Similar transactions modal).
   * Supplier drafts prefer merchant_name; classification links to pending supplier draft canonical_name when present.
   */
  similar_tx_modal?: boolean;
  /** Modal row used as anchor in generated classification notes. */
  similar_tx_anchor_transaction_id?: number;
  /** Supplier-alias proposal DB ids created in the same flow (for classification draft notes). */
  similar_tx_supplier_proposal_db_ids?: number[];
};

/**
 * POST rule-proposals/v2/generate with transaction ID scope (Similar TX modal, audit buckets, etc.).
 */
export async function postRuleProposalsV2(
  api: AxiosInstance,
  family: RuleProposalFamilyV2,
  transactionIds: number[],
  opts?: PostRuleProposalsV2Options
) {
  const body: Record<string, unknown> = { family };
  const ac = opts?.auditCandidates;
  if (ac?.auditRunId?.trim() && ac.candidateIds?.length) {
    body.source = {
      kind: 'audit_candidates',
      audit_run_id: ac.auditRunId.trim(),
      candidate_ids: ac.candidateIds.map((x) => String(x).trim()).filter((s) => s.length > 0)
    };
  } else {
    body.source = { kind: 'transaction_ids', transaction_ids: transactionIds };
  }
  const uid = opts?.userId;
  if (uid != null && Number.isFinite(Number(uid)) && Number(uid) > 0) {
    body.user_id = Number(uid);
  }
  const scope = (opts?.account_scope || '').trim();
  if (scope) {
    body.account_scope = scope;
  }
  if (opts?.similar_tx_modal) {
    body.similar_tx_modal = true;
  }
  const anchor = opts?.similar_tx_anchor_transaction_id;
  if (anchor != null && Number.isFinite(Number(anchor)) && Number(anchor) > 0) {
    body.similar_tx_anchor_transaction_id = Math.trunc(Number(anchor));
  }
  const sp = opts?.similar_tx_supplier_proposal_db_ids;
  if (Array.isArray(sp) && sp.length > 0) {
    const ids = sp
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n) && n > 0)
      .map((n) => Math.trunc(n));
    if (ids.length > 0) {
      body.similar_tx_supplier_proposal_db_ids = ids;
    }
  }
  return api.post(RULE_PROPOSALS_V2_GENERATE_URL, body);
}

/** UI copy: maps API ``skipped_*`` counters to short reasons (classification family). */
const CLASSIFICATION_SKIP_LABELS: ReadonlyArray<readonly [string, string]> = [
  ['skipped_no_granular_tag', 'no granular category (from tags or inference)'],
  ['skipped_no_match_text', 'no supplier/description text'],
  ['skipped_rule_applied', 'already classified by applied rules'],
  ['skipped_matching_rule', 'matched an active classification rule'],
  ['skipped_matching_pending_proposal', 'matched a pending proposal'],
  ['skipped_matching_pending_signature', 'duplicate pending proposal signature'],
  ['skipped_matching_rejected_signature', 'matched a rejected proposal signature'],
  ['skipped_existing_proposal_for_tx', 'already has a saved proposal'],
  ['skipped_similar_tx_modal_coalesce', 'shared match criteria consolidated into another draft'],
  ['skipped_existing_active_rule_equivalent', 'would duplicate an active rule'],
  ['skipped_would_conflict_existing_rule', 'would conflict with an active rule on shared TXs'],
  ['skipped_prefer_existing_rule', 'prefer editing an existing related rule'],
  ['skipped_semantic_duplicate', 'semantic duplicate of pending or rejected proposal'],
];

/** Supplier-alias proposal sweep skip labels. */
const SUPPLIER_SKIP_LABELS: ReadonlyArray<readonly [string, string]> = [
  ['skipped_existing_rule', 'already covered by a supplier alias rule'],
  ['skipped_matching_pending_proposal', 'matched a pending supplier proposal'],
  ['skipped_matching_rejected_signature', 'matched a rejected proposal signature'],
  ['skipped_existing_proposal_for_tx', 'already has a supplier proposal'],
  ['skipped_no_supplier_candidate', 'no supplier candidate text'],
  ['skipped_no_match_text', 'no match text'],
  ['skipped_not_missing_supplier', 'supplier already populated (missing-supplier sweep only)'],
  ['skipped_similar_tx_modal_coalesce', 'shared match criteria consolidated into another draft'],
];

function summarizeSkipReasonsFromApiPayload(
  apiData: Record<string, unknown> | null | undefined,
  spec: ReadonlyArray<readonly [string, string]>,
  maxParts: number
): string | null {
  if (!apiData || maxParts <= 0) return null;
  const pairs: Array<{ count: number; label: string }> = [];
  for (const [key, label] of spec) {
    const raw = apiData[key];
    const count = typeof raw === 'number' && Number.isFinite(raw) ? raw : Number(raw || 0);
    if (!Number.isFinite(count) || count <= 0) continue;
    pairs.push({ count, label });
  }
  pairs.sort((a, b) => b.count - a.count);
  if (!pairs.length) return null;
  return pairs
    .slice(0, maxParts)
    .map(({ count, label }) => `${count.toLocaleString()}× ${label}`)
    .join('; ');
}

/** Where the generation was triggered — shown at the start of toast lines. */
export const RULE_DRAFT_TOAST_ORIGINS = {
  similarTransactionsSelected: 'Similar transactions (your selected rows)',
  ruleMatchingModal: 'Rule matching modal'
} as const;

/**
 * One-line toast for POST rule-proposals v2/classification targeting specific transactions.
 */
export function formatClassificationDraftToastLine(opts: {
  origin: string;
  scanned: number;
  created: number;
  apiData?: Record<string, unknown> | null;
}): string {
  const { origin, scanned, created, apiData } = opts;
  const txs = scanned === 1 ? 'transaction' : 'transactions';
  const skips = summarizeSkipReasonsFromApiPayload(apiData, CLASSIFICATION_SKIP_LABELS, 4);
  const rulesHint = 'Check Rules ▸ Rule proposals (status Proposed).';

  if (created > 0) {
    const prop = created === 1 ? 'proposal' : 'proposals';
    let line = `${origin}: created ${created} classification ${prop} (${scanned} ${txs} sent to the API). ${rulesHint}`;
    if (skips) {
      line += ` Other rows skipped: ${skips}.`;
    }
    return line;
  }

  let line = `${origin}: no new classification drafts — API scanned ${scanned} selected ${txs} and created 0 proposals.`;
  if (skips) {
    line += ` Skipped breakdown: ${skips}.`;
  } else {
    line += ' Tip: add a granular category + VAT from the modal (bulk tag) or run rules first, then try again.';
  }
  line += ` ${rulesHint}`;
  return line;
}

/**
 * One-line toast for supplier-alias drafts from scoped transaction IDs.
 */
export function formatSupplierDraftToastLine(opts: {
  origin: string;
  scanned: number;
  created: number;
  apiData?: Record<string, unknown> | null;
}): string {
  const { origin, scanned, created, apiData } = opts;
  const txs = scanned === 1 ? 'transaction' : 'transactions';
  const skips = summarizeSkipReasonsFromApiPayload(apiData, SUPPLIER_SKIP_LABELS, 4);
  const rulesHint = 'Check Rules ▸ Supplier proposals (status Proposed).';

  if (created > 0) {
    const prop = created === 1 ? 'proposal' : 'proposals';
    let line = `${origin}: created ${created} supplier ${prop} (${scanned} ${txs} sent to the API). ${rulesHint}`;
    if (skips) line += ` Other rows skipped: ${skips}.`;
    return line;
  }

  let line = `${origin}: no new supplier drafts — API scanned ${scanned} selected ${txs} and created 0 proposals.`;
  if (skips) {
    line += ` Skipped breakdown: ${skips}.`;
  }
  line += ` ${rulesHint}`;
  return line;
}
